import React, { useEffect, useState } from "react"

/**
 * StayEstimator — « Estimer mon séjour », calcul par fourchette officielle.
 *
 * Aucun tarif en temps réel : la maison pratique des prix dynamiques. Le
 * calcul part de la fourchette communiquée (par suite et par nuit) et ajoute
 * petit-déjeuner et taxe de séjour par adulte. Rien n'est envoyé : le CTA
 * appelle `onRequest(prefill)`, la page ouvre son propre formulaire pré-rempli.
 *
 * rates = { min, max, breakfast: [{ key, price }], tax, maxSuites, privatisationFrom }
 */

const TEXT = {
  fr: {
    numLocale: "fr-FR",
    privGuests: "privatisation",
    arrival: "Arrivée",
    departure: "Départ",
    adults: "Adultes",
    children: "Enfants",
    suites: "Suites",
    breakfast: "Petit-déjeuner",
    perAdult: "par adulte et par jour",
    bf: { none: "Aucun", continental: "Continental", maison: "Maison Bauchart" },
    less: what => `Retirer : ${what}`,
    more: what => `Ajouter : ${what}`,
    dateError: "La date de départ doit suivre la date d'arrivée.",
    pickDates: "Choisissez vos dates d'arrivée et de départ.",
    nights: n => `${n} nuit${n > 1 ? "s" : ""}`,
    suitesN: n => `${n} suite${n > 1 ? "s" : ""}`,
    estimate: "Total estimé",
    lodging: "Hébergement",
    lodgingSub: (min, max) => `${min} à ${max} par suite et par nuit`,
    breakfastLine: "Petit-déjeuner",
    taxLine: "Taxe de séjour",
    taxSub: t => `${t} par adulte et par nuit`,
    capacity: "Au-delà de quatre personnes par suite, ajoutez une suite ou choisissez un appartement.",
    note: "Estimation indicative : la maison pratique des tarifs dynamiques selon la saison. Votre conseiller confirme le tarif exact et la disponibilité pour vos dates.",
    cta: "Recevoir le tarif exact pour ces dates",
    privTitle: "Privatisation de la maison entière",
    privBody: from => `À partir de sept suites, la maison se privatise : les dix suites et les espaces communs, pour vous seuls. À partir de ${from} par nuit, deux nuits minimum en basse saison.`,
    privCta: "Demander la privatisation",
    eyebrow: "Estimer mon séjour",
    resultEyebrow: "Votre estimation",
    msg: (o) => `Estimation : ${o.range} pour ${o.nights}, ${o.suites}, ${o.adults} adulte${o.a > 1 ? "s" : ""}${o.c ? `, ${o.c} enfant${o.c > 1 ? "s" : ""}` : ""}, petit-déjeuner : ${o.bf}.`,
    privMsg: (o) => `Privatisation de la maison entière (${o.suites}+ suites demandées), ${o.adults} adulte${o.a > 1 ? "s" : ""}${o.c ? `, ${o.c} enfant${o.c > 1 ? "s" : ""}` : ""}${o.nights ? `, ${o.nights}` : ""}.`,
    datesFmt: (a, d) => `${a} → ${d}`,
  },
  en: {
    numLocale: "en-GB",
    privGuests: "buyout",
    arrival: "Check-in",
    departure: "Check-out",
    adults: "Adults",
    children: "Children",
    suites: "Suites",
    breakfast: "Breakfast",
    perAdult: "per adult, per day",
    bf: { none: "None", continental: "Continental", maison: "Maison Bauchart" },
    less: what => `Remove: ${what}`,
    more: what => `Add: ${what}`,
    dateError: "Check-out must be after check-in.",
    pickDates: "Choose your check-in and check-out dates.",
    nights: n => `${n} night${n > 1 ? "s" : ""}`,
    suitesN: n => `${n} suite${n > 1 ? "s" : ""}`,
    estimate: "Estimated total",
    lodging: "Accommodation",
    lodgingSub: (min, max) => `${min} to ${max} per suite, per night`,
    breakfastLine: "Breakfast",
    taxLine: "City tax",
    taxSub: t => `${t} per adult, per night`,
    capacity: "Beyond four guests per suite, add a suite or choose an apartment.",
    note: "An indicative estimate: the house uses dynamic, season-led pricing. Your advisor confirms the exact rate and availability for your dates.",
    cta: "Get the exact rate for these dates",
    privTitle: "The whole house, privately",
    privBody: from => `From seven suites, the house is booked in full: all ten suites and the shared spaces, for you alone. From ${from} a night, two nights minimum in low season.`,
    privCta: "Ask about a full buy-out",
    eyebrow: "Estimate my stay",
    resultEyebrow: "Your estimate",
    msg: (o) => `Estimate: ${o.range} for ${o.nights}, ${o.suites}, ${o.adults} adult${o.a > 1 ? "s" : ""}${o.c ? `, ${o.c} child${o.c > 1 ? "ren" : ""}` : ""}, breakfast: ${o.bf}.`,
    privMsg: (o) => `Whole-house buy-out (${o.suites}+ suites requested), ${o.adults} adult${o.a > 1 ? "s" : ""}${o.c ? `, ${o.c} child${o.c > 1 ? "ren" : ""}` : ""}${o.nights ? `, ${o.nights}` : ""}.`,
    datesFmt: (a, d) => `${a} → ${d}`,
  },
}

const STYLE = `
  .se-wrap { display:grid; grid-template-columns:minmax(0,1.05fr) minmax(0,1fr); gap:16px; margin:8px 0 20px; }
  .se-card { background:#fff; border-radius:24px; padding:26px 24px; box-shadow:0 1px 2px rgba(0,0,0,0.04),0 8px 28px rgba(0,0,0,0.05); min-width:0; }
  .se-eyebrow { display:block; font-size:12px; font-weight:600; color:#0071e3; letter-spacing:0.02em; margin-bottom:16px; }
  .se-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:16px; }
  .se-field { min-width:0; }
  .se-field > label, .se-legend { display:block; font-size:12px; font-weight:600; color:#6e6e73; margin-bottom:6px; padding:0; }
  .se-date { display:block; width:100%; min-width:0; box-sizing:border-box; min-height:46px; padding:10px 12px; border-radius:14px; border:1px solid rgba(0,0,0,0.12); background:#f5f5f7; font:inherit; font-size:15px; color:#1d1d1f; -webkit-appearance:none; appearance:none; }
  .se-date:focus { outline:none; border-color:#0071e3; background:#fff; box-shadow:0 0 0 3px rgba(0,113,227,0.18); }
  .se-steppers { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; margin-bottom:16px; }
  .se-stepper { display:flex; align-items:center; justify-content:space-between; gap:4px; background:#f5f5f7; border-radius:999px; padding:4px; min-height:46px; box-sizing:border-box; }
  .se-stepper button { flex:0 0 auto; width:36px; height:36px; border-radius:50%; border:none; background:#fff; color:#1d1d1f; font-size:18px; line-height:1; cursor:pointer; box-shadow:0 1px 2px rgba(0,0,0,0.08); }
  .se-stepper button:disabled { opacity:0.35; cursor:default; }
  .se-stepper button:focus-visible { outline:2px solid #0071e3; outline-offset:2px; }
  .se-stepper output { font-size:17px; font-weight:600; letter-spacing:-0.02em; min-width:22px; text-align:center; }
  .se-fieldset { border:none; margin:0; padding:0; min-width:0; }
  .se-pills { display:flex; flex-wrap:wrap; gap:8px; }
  .se-pill { position:relative; }
  .se-pill input { position:absolute; opacity:0; width:1px; height:1px; }
  .se-pill span { display:inline-flex; align-items:center; gap:6px; min-height:40px; padding:0 14px; border-radius:999px; border:1px solid rgba(0,0,0,0.12); background:#fff; font-size:14px; font-weight:500; color:#1d1d1f; cursor:pointer; transition:background 180ms,color 180ms,border-color 180ms; }
  .se-pill span em { font-style:normal; color:#6e6e73; font-weight:400; }
  .se-pill input:checked + span { background:#1d1d1f; border-color:#1d1d1f; color:#fff; }
  .se-pill input:checked + span em { color:rgba(255,255,255,0.7); }
  .se-pill input:focus-visible + span { outline:2px solid #0071e3; outline-offset:2px; }
  .se-hint { font-size:12px; color:#6e6e73; margin-top:8px; }
  .se-error { font-size:13px; color:#c0392b; margin:-6px 0 14px; }
  .se-total { font-size:clamp(30px,3.6vw,42px); font-weight:700; letter-spacing:-0.045em; line-height:1.05; color:#1d1d1f; margin:2px 0 6px; }
  .se-total-sub { font-size:14px; color:#6e6e73; margin-bottom:18px; }
  .se-lines { border-top:1px solid rgba(0,0,0,0.08); margin:0 0 16px; padding:0; list-style:none; }
  .se-lines li { display:flex; justify-content:space-between; align-items:baseline; gap:12px; padding:12px 0; border-bottom:1px solid rgba(0,0,0,0.06); font-size:15px; color:#1d1d1f; }
  .se-lines li small { display:block; font-size:12px; color:#6e6e73; margin-top:2px; }
  .se-lines li b { font-weight:600; white-space:nowrap; text-align:right; }
  .se-note { font-size:12px; line-height:1.55; color:#6e6e73; margin:0 0 16px; }
  .se-warn { font-size:13px; color:#1d1d1f; background:rgba(0,113,227,0.06); border-radius:12px; padding:10px 12px; margin-bottom:14px; }
  .se-cta { width:100%; white-space:normal !important; text-align:center; line-height:1.25; padding-top:10px !important; padding-bottom:10px !important; }
  .se-empty { font-size:15px; color:#6e6e73; padding:18px 0; }
  @media (max-width:820px){ .se-wrap { grid-template-columns:minmax(0,1fr); } }
  @media (max-width:733.98px){ .se-stepper button { width:40px; height:40px; } }
  @media (max-width:520px){
    .se-steppers { grid-template-columns:minmax(0,1fr); gap:8px; }
    .se-steppers .se-field { display:flex; align-items:center; justify-content:space-between; gap:12px; }
    .se-steppers .se-field > label { margin:0; font-size:15px; font-weight:500; color:#1d1d1f; }
    .se-steppers .se-stepper { width:140px; flex:0 0 auto; }
    .se-card { padding:22px 18px; }
  }
`

const toISO = d => {
  const z = n => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`
}
const nightsBetween = (a, d) => {
  if (!a || !d) return 0
  const [y1, m1, d1] = a.split("-").map(Number)
  const [y2, m2, d2] = d.split("-").map(Number)
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000)
}
const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number)
  return toISO(new Date(y, m - 1, d + n))
}

const Stepper = ({ id, label, value, min, max, onChange, t, display }) => (
  <div className="se-field">
    <label id={`${id}-l`} htmlFor={`${id}-o`}>{label}</label>
    <div className="se-stepper" role="group" aria-labelledby={`${id}-l`}>
      <button type="button" aria-label={t.less(label)} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}>−</button>
      <output id={`${id}-o`} aria-live="polite">{display ? display(value) : value}</output>
      <button type="button" aria-label={t.more(label)} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}>+</button>
    </div>
  </div>
)

const StayEstimator = ({ locale = "fr", rates, onRequest, idPrefix = "se" }) => {
  const t = TEXT[locale] || TEXT.fr
  const [arrival, setArrival] = useState("")
  const [departure, setDeparture] = useState("")
  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)
  const [suites, setSuites] = useState(1)
  const [bf, setBf] = useState("none")

  // Dates par défaut posées après le montage (pas de décalage SSR / hydratation)
  useEffect(() => {
    const a = new Date()
    a.setDate(a.getDate() + 30)
    const iso = toISO(a)
    setArrival(iso)
    setDeparture(addDays(iso, 3))
  }, [])

  const nf = new Intl.NumberFormat(t.numLocale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
  const nf2 = new Intl.NumberFormat(t.numLocale, { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const money = v => (Math.round(v * 100) % 100 === 0 ? nf.format(v) : nf2.format(v))

  const nights = nightsBetween(arrival, departure)
  const datesOk = arrival && departure && nights > 0
  const priv = suites > rates.maxSuites
  const bfPrice = (rates.breakfast.find(b => b.key === bf) || { price: 0 }).price
  const lodgingMin = rates.min * suites * nights
  const lodgingMax = rates.max * suites * nights
  const breakfastTotal = adults * nights * bfPrice
  const taxTotal = Math.round(rates.tax * adults * nights * 100) / 100
  const totalMin = lodgingMin + breakfastTotal + taxTotal
  const totalMax = lodgingMax + breakfastTotal + taxTotal
  const overCapacity = !priv && adults + children > suites * 4
  const range = `${money(totalMin)} – ${money(totalMax)}`
  const fmtDate = iso => {
    if (!iso) return ""
    const [y, m, d] = iso.split("-").map(Number)
    return new Date(y, m - 1, d).toLocaleDateString(t.numLocale, { day: "numeric", month: "long", year: "numeric" })
  }
  const guestsOption = () => {
    const n = adults + children
    if (priv) return t.privGuests
    if (n <= 2) return "2"
    if (n <= 4) return "3-4"
    if (n <= 6) return "5-6"
    return "7+"
  }

  const request = () => {
    if (!onRequest) return
    const o = { range, nights: datesOk ? t.nights(nights) : "", suites: priv ? rates.maxSuites + 1 : t.suitesN(suites), adults, a: adults, c: children, bf: t.bf[bf] }
    onRequest({
      dates: datesOk ? t.datesFmt(fmtDate(arrival), fmtDate(departure)) : "",
      guests: guestsOption(),
      message: priv ? t.privMsg(o) : t.msg(o),
    })
  }

  return (
    <div className="se-wrap">
      <style>{STYLE}</style>
      <div className="se-card">
        <span className="se-eyebrow">{t.eyebrow}</span>
        <div className="se-row">
          <div className="se-field">
            <label htmlFor={`${idPrefix}-arrival`}>{t.arrival}</label>
            <input id={`${idPrefix}-arrival`} className="se-date" type="date" value={arrival} onChange={e => {
              const v = e.target.value
              setArrival(v)
              if (v && (!departure || nightsBetween(v, departure) <= 0)) setDeparture(addDays(v, 1))
            }} />
          </div>
          <div className="se-field">
            <label htmlFor={`${idPrefix}-departure`}>{t.departure}</label>
            <input id={`${idPrefix}-departure`} className="se-date" type="date" value={departure} min={arrival ? addDays(arrival, 1) : undefined} aria-invalid={arrival && departure && nights <= 0 ? "true" : undefined} onChange={e => setDeparture(e.target.value)} />
          </div>
        </div>
        {arrival && departure && nights <= 0 && <p className="se-error" role="alert">{t.dateError}</p>}
        <div className="se-steppers">
          <Stepper id={`${idPrefix}-adults`} label={t.adults} value={adults} min={1} max={30} onChange={setAdults} t={t} />
          <Stepper id={`${idPrefix}-children`} label={t.children} value={children} min={0} max={20} onChange={setChildren} t={t} />
          <Stepper id={`${idPrefix}-suites`} label={t.suites} value={suites} min={1} max={rates.maxSuites + 1} onChange={setSuites} t={t} display={v => (v > rates.maxSuites ? `${v}+` : v)} />
        </div>
        <fieldset className="se-fieldset">
          <legend className="se-legend">{t.breakfast}</legend>
          <div className="se-pills">
            {[{ key: "none", price: 0 }, ...rates.breakfast].map(b => (
              <label key={b.key} className="se-pill">
                <input type="radio" name={`${idPrefix}-bf`} value={b.key} checked={bf === b.key} onChange={() => setBf(b.key)} />
                <span>{t.bf[b.key]}{b.price ? <em>{money(b.price)}</em> : null}</span>
              </label>
            ))}
          </div>
          <div className="se-hint">{t.perAdult}</div>
        </fieldset>
      </div>

      <div className="se-card" aria-live="polite">
        <span className="se-eyebrow">{t.resultEyebrow}</span>
        {priv ? (
          <>
            <div className="se-total" style={{ fontSize: "clamp(24px,2.8vw,32px)" }}>{t.privTitle}</div>
            <p className="se-total-sub" style={{ lineHeight: 1.55 }}>{t.privBody(money(rates.privatisationFrom))}</p>
            <button type="button" className="ios-cta ios-cta-primary se-cta" onClick={request}>{t.privCta}</button>
          </>
        ) : !datesOk ? (
          <div className="se-empty">{arrival && departure ? t.dateError : t.pickDates}</div>
        ) : (
          <>
            <div className="se-total" data-se-total="">{range}</div>
            <div className="se-total-sub">{t.estimate} · {t.nights(nights)} · {t.suitesN(suites)}</div>
            <ul className="se-lines">
              <li><span>{t.lodging}<small>{t.lodgingSub(money(rates.min), money(rates.max))}</small></span><b data-se="lodging">{money(lodgingMin)} – {money(lodgingMax)}</b></li>
              <li><span>{t.breakfastLine}<small>{bfPrice ? `${adults} × ${t.nights(nights)} × ${money(bfPrice)}` : t.bf.none}</small></span><b data-se="breakfast">{money(breakfastTotal)}</b></li>
              <li><span>{t.taxLine}<small>{t.taxSub(money(rates.tax))}</small></span><b data-se="tax">{money(taxTotal)}</b></li>
            </ul>
            {overCapacity && <div className="se-warn">{t.capacity}</div>}
            <p className="se-note">{t.note}</p>
            <button type="button" className="ios-cta ios-cta-primary se-cta" onClick={request}>{t.cta}</button>
          </>
        )}
      </div>
    </div>
  )
}

export default StayEstimator
